<template>
  <FilesSkeleton v-if="isLoading" />
  <div
    v-else
    class="tw-h-screen tw-min-w-[23.4rem] tw-overflow-auto tw-rounded-2xl tw-bg-white tw-px-6 tw-py-8 tw-pt-8 sm:tw-h-full"
  >
    <!-- Header -->
    <FilesHeader title="My files" />

    <!-- Search and Filter Bar -->
    <FilesSearchFilterBar
      v-if="hasFiles"
      v-model:search-value="searchValue"
      v-model:selected-status="selectedStatus"
      v-model:sort-direction="sortDirection"
      :status-options="statusOptions"
    />

    <FilesFilterChips
      v-if="hasFiles"
      :selected-status="selectedStatus"
      :sort-direction="sortDirection"
      @remove-status="removeStatus"
      @remove-sort-direction="clearSortDirection"
      @clear-all="clearAllFilters"
    />

    <!-- Empty State - No Files -->
    <div v-if="isSuccess && !hasFiles" class="tw-mt-16">
      <EmptyState
        title="No files uploaded"
        description="Files will appear here after you complete actions on the homepage."
        :icon="FolderIcon"
      >
        <template #icon>
          <FolderIcon class="tw-h-12 tw-w-12" :class="emptyStateFolderIcon" />
        </template>
      </EmptyState>
    </div>

    <NotFoundState
      v-else-if="isSuccess && filteredActions?.length === 0"
      text="No files found. Try adjusting your filters or search criteria, or check back later for new files."
      :icon="MagnifyingGlassIconEmptyState"
      class="tw-mt-12"
    />

    <!-- File List -->
    <FilesFileList :actions="filteredActions" :chip-labels="chipLabels" />
  </div>
</template>

<script setup lang="ts">
import { ActionRequestStatus, ActionRequestSubType } from '@torticity/types'
import moment from 'moment'

import FolderIcon from '~icons/fluent/folder-24-filled'
import MagnifyingGlassIconEmptyState from '~icons/fluent/search-24-regular'

definePageMeta({
  layout: 'blank',
})

const { getThemeTokenString } = useTheme()
const { shouldSkipPii } = useSkipPii()

const emptyStateFolderIcon = getThemeTokenString('secondary-text')

const chipLabels = computed<Record<ActionRequestStatus, string>>(() => ({
  [ActionRequestStatus.SCREENED]: 'Approved',
  [ActionRequestStatus.SIGNATURE_DECLINED]: 'Rejected',
  [ActionRequestStatus.NEEDS_SCREENING]: 'In review',
  // For wildfire cases, show "Uploaded" instead of "Action needed" for PENDING_USER_ACTION
  [ActionRequestStatus.PENDING_USER_ACTION]: shouldSkipPii.value ? 'Uploaded' : 'Action needed',
}))

const statusOptions = computed(() => {
  const baseOptions = [
    { code: ActionRequestStatus.SCREENED, name: chipLabels.value[ActionRequestStatus.SCREENED] },
    {
      code: ActionRequestStatus.SIGNATURE_DECLINED,
      name: chipLabels.value[ActionRequestStatus.SIGNATURE_DECLINED],
    },
    {
      code: ActionRequestStatus.NEEDS_SCREENING,
      name: chipLabels.value[ActionRequestStatus.NEEDS_SCREENING],
    },
  ]

  // For wildfire cases, include PENDING_USER_ACTION so users can see their uploaded documents
  if (shouldSkipPii.value) {
    baseOptions.push({
      code: ActionRequestStatus.PENDING_USER_ACTION,
      name: chipLabels.value[ActionRequestStatus.PENDING_USER_ACTION],
    })
  }

  return baseOptions
})

const searchValue = ref('')
const selectedStatus = ref<{ code: ActionRequestStatus; name: string }[]>([])
const sortDirection = ref<'asc' | 'desc' | null>(null)

const availableStatuses = computed(() => statusOptions.value.map(option => option.code))

const {
  data: actions,
  isSuccess,
  isLoading,
} = useActionRequestsQuery(null, unref(availableStatuses))
const actionsWithDocuments = computed(() => {
  return (
    actions?.value?.map(action => {
      const documents =
        // @ts-expect-error
        (action?.documents as unknown as {
          id: string
          fileName: string
          fileType: string
          createdAt: string | Date
          signedUrl: string
          isSensitive?: boolean
        }[]) || []

      if (action.subType === ActionRequestSubType.DIRECT_DEPOSIT) {
        documents.push({
          id: action.id,
          fileName: 'Direct Deposit',
          fileType: 'direct-deposit',
          createdAt: action.createdAt as Date,
          signedUrl: '',
        })
      }

      return {
        ...action,
        documents,
      }
    }) || []
  )
})

const hasFiles = computed(() => {
  if (!actionsWithDocuments.value || actionsWithDocuments.value.length === 0) return false

  return actionsWithDocuments.value.some(action => action?.documents && action.documents.length > 0)
})

const filteredActions = computed(() => {
  if (!actionsWithDocuments.value) return []

  // First, filter the actions and their documents
  const result = actionsWithDocuments.value
    .map(action => {
      if (!action) return null

      if (
        selectedStatus.value.length > 0 &&
        !selectedStatus.value.some(s => s.code === action.status)
      ) {
        return null
      }

      if (searchValue.value.trim()) {
        const searchLower = searchValue.value.toLowerCase().trim()

        const filteredDocuments = action.documents?.filter(
          doc =>
            doc.fileName.toLowerCase().includes(searchLower) ||
            action.subType?.toLowerCase().includes(searchLower) ||
            (typeof action.type === 'string' && action.type.toLowerCase().includes(searchLower)) ||
            moment(doc.createdAt).format('MMMM DD, YYYY').toLowerCase().includes(searchLower),
        )

        if (!filteredDocuments?.length) {
          return null
        }

        return {
          ...action,
          documents: filteredDocuments,
        }
      }

      return action
    })
    .filter(Boolean)

  // Collect all documents from all actions with proper type assertion
  const allDocuments = result.flatMap(action => {
    // Ensure action is treated as non-null after filter(Boolean)
    const nonNullAction = action as NonNullable<typeof action>
    return (nonNullAction.documents || []).map(doc => ({
      ...doc,
      actionId: nonNullAction.id,
      actionSubType: nonNullAction.subType || '',
      actionStatus: nonNullAction.status,
    }))
  })

  // Sort all documents
  const sortedDocuments = allDocuments.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime()
    const dateB = new Date(b.createdAt).getTime()
    return sortDirection.value === 'asc' ? dateA - dateB : dateB - dateA
  })

  // Group documents back into their actions
  const actionMap = new Map()

  sortedDocuments.forEach(doc => {
    const actionId = doc.actionId
    if (!actionMap.has(actionId)) {
      const originalAction = result.find(a => a?.id === actionId)
      if (!originalAction) return

      actionMap.set(actionId, {
        ...originalAction,
        documents: [],
      })
    }
    const action = actionMap.get(actionId)
    if (!action) return

    action.documents.push({
      id: doc.id,
      fileName: doc.fileName,
      fileType: doc.fileType,
      createdAt: doc.createdAt,
      signedUrl: doc.signedUrl,
      isSensitive: doc.isSensitive,
    })
  })

  return Array.from(actionMap.values())
})

function removeStatus(status: { code: ActionRequestStatus; name: string }) {
  selectedStatus.value = selectedStatus.value.filter(s => s.code !== status.code)
}

function clearAllStatuses() {
  selectedStatus.value = []
  console.log('selectedStatus', selectedStatus.value)
}

function clearSortDirection() {
  sortDirection.value = null
}

function clearAllFilters() {
  clearAllStatuses()
  clearSortDirection()
}
</script>
